// src/context/NotificationContext.tsx
import React, { createContext, useContext, useState, ReactNode } from "react";

export type NotifType = "battery-low" | "battery-medium" | "battery-ok" | "system" | "alert";

export interface Notif {
  id: number;
  type: NotifType;
  message: string;
  time: string;
  read: boolean;
}

interface NotificationContextType {
  notifications: Notif[];
  addNotification: (notif: Notif) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  markOneAsRead: (id: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notif[]>([]);

  const addNotification = (notif: Notif) =>
    setNotifications((prev) => [notif, ...prev]);

  const markAllAsRead = () =>
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

  const clearAll = () => setNotifications([]);

  const markOneAsRead = (id: number) =>
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );

  return (
    <NotificationContext.Provider
      value={{ notifications, addNotification, markAllAsRead, clearAll, markOneAsRead }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error("useNotifications must be used within a NotificationProvider");
  return context;
};
